import { PAGE_PATH_HOME, PRODUCT_TYPE_GIFTCARD, PRODUCT_TYPE_GROUPED } from '@bluheadless/ui-logic/src/constants'
import ProductProvider from '@bluheadless/ui-logic/src/providers/product'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useProductUrlParams } from '@bluheadless/ui-logic/src/hooks/useProductUrlParams'
import { useRecentlyViewedProductAdd } from '@bluheadless/ui-logic/src/hooks/product/useRecentlyViewedProductAdd'
import { Grid } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/system'
import { number } from 'prop-types'
import { useCallback, useRef, useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import ComparableProducts from '../../organisms/comparable-products'
import Features from '../../organisms/product/features'
import Gallery from '../../organisms/product/gallery'
import PrevNext from '../../organisms/product/prev-next'
import SidebarProduct from '../../organisms/product/sidebar/sidebar'
import SidebarGroupedProduct from '../../organisms/product/sidebar/sidebar-grouped'
import RecentlyViewedProducts from '../../organisms/recently-viewed'
import Related from '../../organisms/related'
import Reviews from '../../organisms/reviews'
import Upsells from '../../organisms/upsells'
import DynamicAreas from '../../particles/dynamic-areas'
import ProductOptionsToUrlParams from '../../particles/product-options-to-url-params'
import RichSnippetProduct from '../../particles/rich-snippets/product'
import { GridContainer, GridItem, StickyStyled, BreadcrumbsStyled } from './product.styled'
import ProductTrackings from '../../organisms/product/trackings'
import { useProductBreadcrumbs } from '@bluheadless/ui-logic/src/hooks/product/useProductBreadcrumbs'
import { useClerkScript } from '@bluheadless/ui-logic/src/hooks/clerk/useClerkScript'
import StickyButtons from '../../organisms/product/sidebar/sticky-buttons'
import { useInView } from 'react-intersection-observer'
import ButtonsAction from '../../organisms/product/sidebar/buttons-action'
import { useStore } from '@bluheadless/ui-logic/src/providers/store'
import { useCatalogUrl } from '@bluheadless/ui-logic/src/hooks/useCatalogUrl'
import { removeLeadingSlashes } from '@bluheadless/ui-logic/src/utils/url'

const ProductPageComponent = ({ entityId, ..._product }) => {
	useClerkScript()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const product = useMemo(() => _product, [_product.id])

	const { formatMessage } = useIntl()
	const theme = useTheme()
	const mdUp = useMediaQuery(theme.breakpoints.up('md'))
	const smUp = useMediaQuery(theme.breakpoints.up('sm'))

	const {
		catalog: {
			products: {
				recentlyViewed: { enabled: recentlyViewedEnabled },
				stickyBox,
				comparable: { enabled: comparableEnabled },
				stickyAddToCart,
			},
		},
	} = useConfig()

	useRecentlyViewedProductAdd({ id: entityId })

	const { productBreadcrumbs } = useProductBreadcrumbs()

	const breadcrumbsItems = [
		{ title: formatMessage({ id: 'breadcrumbs_home' }), url: PAGE_PATH_HOME },
		...productBreadcrumbs,
		{ title: product.name },
	]

	const reviewsRef = useRef()

	const columnsGalleryByProductType = useCallback(() => {
		switch (product.type) {
			case PRODUCT_TYPE_GROUPED:
				return 6
			case PRODUCT_TYPE_GIFTCARD:
				return 6
			default:
				return 6
		}
	}, [product.type])

	const columnsSidebarByProductType = useCallback(() => {
		switch (product.type) {
			case PRODUCT_TYPE_GROUPED:
				return 6
			case PRODUCT_TYPE_GIFTCARD:
				return 6
			default:
				return 6
		}
	}, [product.type])

	const sidebarPaddingLeft = useCallback(() => {
		switch (product.type) {
			case PRODUCT_TYPE_GROUPED:
			case PRODUCT_TYPE_GIFTCARD:
				return '10%'
			default:
				return '32px'
		}
	}, [product.type])

	const formDefaultValues = useProductUrlParams({ product })

	const sidebarRef = useRef(null)
	const stickyRef = useRef(null)

	useEffect(() => {
		if (!sidebarRef.current) return
		if (!stickyRef.current) return
		const resizeObserver = new ResizeObserver(() => {
			stickyRef.current.updateInitialDimension()
			stickyRef.current.update()
		})
		resizeObserver.observe(sidebarRef.current)
		return () => resizeObserver.disconnect()
	}, [])

	const [inViewRef, inView] = useInView({
		threshold: 0.5,
		triggerOnce: false,
	})

	const { uri: storeUri } = useStore()

	const productUrl = useCatalogUrl(product.urlKey)

	return (
		<>
			<RichSnippetProduct product={product} />
			<ProductProvider product={product} formDefaultValues={formDefaultValues}>
				<ProductTrackings />
				<ProductOptionsToUrlParams />
				<GridContainer
					container
					pt={2}
					id="product-grid-container"
					data-product-type={product.type}
					data-product-sku={product.sku}
					data-product-url={product.urlKey ? storeUri + removeLeadingSlashes(productUrl.pathname) : ''}
					data-product-name={product.name}
					data-product-base-price={product.prices?.old ? parseFloat(product.prices.old) : ''}
					data-product-special-price={
						product.prices?.final < product.prices?.old ? parseFloat(product.prices.final) : ''
					}
				>
					<GridItem item xs={12} pl={mdUp && sidebarPaddingLeft()} order={mdUp ? '2' : '3'} mt={mdUp ? '0' : 2}>
						<PrevNext />
					</GridItem>

					<Grid item xs={12} md={columnsGalleryByProductType()} order={mdUp ? '3' : '2'} id="sidebar-gallery">
						<StickyStyled
							offset={20}
							enabled={mdUp && stickyBox[product.type] == 'gallery'}
							bottomBoundary={'#sidebar-gallery'}
							ref={stickyRef}
						>
							<Gallery />
						</StickyStyled>
					</Grid>
					<GridItem
						id="sidebar-product"
						item
						xs={12}
						md={columnsSidebarByProductType()}
						pl={mdUp && sidebarPaddingLeft()}
						pt={mdUp && '55px'}
						order="4"
					>
						{product.type == PRODUCT_TYPE_GROUPED ? (
							<SidebarGroupedProduct ref={sidebarRef} reviewsRef={reviewsRef} />
						) : (
							<StickyStyled
								offset={20}
								enabled={mdUp && stickyBox[product.type] == 'sidebar'}
								bottomBoundary={'#sidebar-product'}
								ref={stickyRef}
							>
								<SidebarProduct ref={sidebarRef} reviewsRef={reviewsRef} inViewRef={inViewRef} />
							</StickyStyled>
						)}
					</GridItem>
				</GridContainer>

				<Features />

				<DynamicAreas areas={product.dynamicAreas} container="middle" />

				<Related />
				<Upsells />

				{recentlyViewedEnabled && <RecentlyViewedProducts productId={entityId} />}

				<DynamicAreas areas={product.dynamicAreas} container="bottom" />

				<Reviews ref={reviewsRef} />

				<BreadcrumbsStyled items={breadcrumbsItems} mb={4} />

				{smUp && (
					<Grid>
						{stickyAddToCart[product.type] && (
							<StickyButtons show={!inView}>
								<ButtonsAction isSticky />
							</StickyButtons>
						)}
					</Grid>
				)}
			</ProductProvider>
			{comparableEnabled && <ComparableProducts />}
		</>
	)
}

ProductPageComponent.defaultProps = {}

ProductPageComponent.propTypes = {
	entityId: number,
}

export default ProductPageComponent
